import axios from "axios";

const moduleProductInventory = {
  namespaced: true,
  state: {
    productList: [],
    allGRWithProductList: [],
    createProductItem: null,
    productListLastUpdated: null,
    stockAdjustmentList: [],
    hqStockAdjustmentList: [],
  },
  mutations: {
    setProductList(state, data) {
      state.productList = data;
    },
    setAllGRWithProductList(state, data) {
      state.allGRWithProductList = data;
    },
    setProductListLastUpdated(state, data) {
      state.productListLastUpdated = data;
    },
    setStockAdjustmentList(state, data) {
      state.stockAdjustmentList = data;
    },
    setHQStockAdjustmentList(state, data) {
      state.hqStockAdjustmentList = data;
    },
    patchHQStockAdjustmentStatus(state, { id, status }) {
      const item = state.hqStockAdjustmentList.find(i => i.id === id);
      if (item) item.status = status;
    },
    removeHQStockAdjustment(state, { id }) {
      state.hqStockAdjustmentList = state.hqStockAdjustmentList.filter(i => i.id !== id);
    },
  },
  actions: {
    async fetchProductList({ commit }, { clinicUrl, branchUrl }) {
      try {
        const res = await axios.get(`/api/stocks/${clinicUrl}/${branchUrl}`);
        const items = res.data.data.map(i => ({
          productId: i.product.id,
          productCode: i.product.no,
          productCategory: i.product.category,
          productNameMain: i.product.description,
          productNameAdditional: i.product.description2,
          productStock: i.qtyAvailable,
          productUnit: i.product.baseUom,
          productPrice: i.avgCost,
          productStatus: i.product.status,
          productPurchaseUnit: i.product.purchaseUom,
          productSalesUnit: i.product.saleUom,
          productConversionUnitItems: i.product.productUomConversions,
          productAvailableStock: i.qtyAvailable,
          imageUrl: i.product.file?.thumbnailURL || i.product.file?.mediaURL || null,
        }));

        const lastUpdated = res.data.lastCutoffStock;

        commit("setProductListLastUpdated", lastUpdated);

        commit("setProductList", items);
        return items;
      } catch (err) {
        console.error("fetchProductList failed", err);
        throw err;
      }
    },
    createProductItem({ commit, getters }, payload) {
      return axios
        .post(
          `/api/master/products/${payload.clinicUrl}/${payload.branchUrl}`,
          payload.adjustProductItem,
          {
            skipGlobalErrorHandler: true,
          }
        )
        .then(res => {
          return res;
        })
        .catch(err => {
          throw err;
        });
    },
    updateProductItem({ commit, getters }, payload) {
      return axios
        .put(
          `/api/master/products/${payload.clinicUrl}/${payload.branchUrl}/${payload.id}`,
          payload.adjustProductItem,
          {
            skipGlobalErrorHandler: true,
          }
        )
        .then(res => {
          return res;
        })
        .catch(err => {
          throw err;
        });
    },
    deleteProductItem({ commit, getters }, payload) {
      return axios
        .delete(
          `/api/master/products/${payload.clinicUrl}/${payload.branchUrl}/${payload.id}`,
          {
            skipGlobalErrorHandler: true,
          }
        )
        .then(res => {
          return res;
        })
        .catch(err => {
          throw err;
        });
    },
    getProductHistoryById({ commit, getters }, payload) {
      return axios
        .get(
          `/api/stockLedgers/${payload.clinicUrl}/${payload.branchUrl}/${payload.id}`
        )
        .then(res => {
          return res;
        })
        .catch(err => {
          // Automatic error handling via interceptor
          throw err;
        });
    },
    createStockAdjustment({ commit, getters }, payload) {
      return axios
        .post(
          `/api/stockAdjustment/${payload.clinicUrl}/${payload.branchUrl}`,
          payload.adjustStockData,
          {
            skipGlobalErrorHandler: true,
          }
        )
        .then(res => {
          return res;
        })
        .catch(err => {
          throw err;
        });
    },
    updateStockAdjustment({ commit, getters }, payload) {
      return axios
        .put(
          `/api/stockAdjustment/${payload.clinicUrl}/${payload.branchUrl}/${payload.adjustmentId}`,
          payload.adjustStockData,
          {
            skipGlobalErrorHandler: true,
          }
        )
        .then(res => {
          return res;
        })
        .catch(err => {
          throw err;
        });
    },
    previewPhysicalCountAdjustment({ commit, getters }, payload) {
      return axios
        .post(
          `/api/stockAdjustment/${payload.clinicUrl}/${payload.branchUrl}/preview`,
          payload.adjustStockData,
          {
            skipGlobalErrorHandler: true,
          }
        )
        .then(res => {
          return res;
        })
        .catch(err => {
          throw err;
        });
    },
    async fetchHQStockAdjustmentList({ commit }, { clinicUrl }) {
      try {
        const res = await axios.get(`/api/approval/${clinicUrl}`, {
          skipGlobalErrorHandler: true,
        });
        const allItems = res.data.data || [];

        const adjItems = allItems
          .filter(item => !item.grNo)
          .map(item => ({
            id: item.id,
            clinicId: item.clinicId,
            branchId: item.branchId,
            branchUrl: item.branch?.code || item.branchUrl || null,
            branchName: item.branch?.name || null,
            adjNo: item.adjNo,
            refDocNo: item.refDocNo,
            reason: item.reason,
            status: item.status,
            creationDt: item.creationDt,
            modifyDt: item.modifyDt,
            creationUid: item.creationUid,
            deletedDt: item.deletedDt,
            syncDt: item.syncDt,
            transactionDt: item.transactionDt,
            type: item.type,
            creationUser: item.creationUser,
          }));

        const grItems = allItems
          .filter(item => !!item.grNo)
          .map(item => ({
            id: item.id,
            grNo: item.grNo || "",
            refDocNo: item.refDocNo || "",
            vendorNo: item.vendorNo || "",
            vendorName: item.vendorName || "",
            branchId: item.branchId,
            branchUrl: item.branch?.code || null,
            branchName: item.branch?.name || "",
            status: item.status || "",
            creationDt: item.creationDt || "",
            receivedDt: item.receivedDt || "",
            needHqApproval: Boolean(item.needHqApproval),
          }));

        commit("setHQStockAdjustmentList", adjItems);
        commit("moduleGoodsReceive/setHQGoodsReceiveList", grItems, { root: true });
        return { adjItems, grItems };
      } catch (err) {
        const status = err?.response?.status;
        if (status === 403) {
          // User's role isn't involved in HQ approval — this is a silent
          // sidebar prefetch, so show nothing (no toast / stale-permissions
          // modal) and treat it as an empty list.
          commit("setHQStockAdjustmentList", []);
          commit("moduleGoodsReceive/setHQGoodsReceiveList", [], { root: true });
          return { adjItems: [], grItems: [] };
        }
        console.error("fetchHQStockAdjustmentList failed", err);
        throw err;
      }
    },
    hqApproveStockAdjustment(_, { clinicUrl, branchUrl, adjustmentId, action, rejectReason }) {
      const body = { action, type: "ADJUSTMENT" };
      if (rejectReason) body.rejectReason = rejectReason;
      return axios
        .post(
          `/api/stockAdjustment/${clinicUrl}/${branchUrl}/${adjustmentId}/hq-approve`,
          body,
          { skipGlobalErrorHandler: true }
        )
        .then(res => res)
        .catch(err => { throw err; });
    },
    approveStockAdjustment(_, { clinicUrl, branchUrl, adjustmentId }) {
      return axios
        .post(
          `/api/stockAdjustment/${clinicUrl}/${branchUrl}/${adjustmentId}/approve`,
          { action: "APPROVE" },
          { skipGlobalErrorHandler: true }
        )
        .then(res => res)
        .catch(err => { throw err; });
    },
    deleteStockAdjustment(_, payload) {
      return axios
        .delete(
          `/api/stockAdjustment/${payload.clinicUrl}/${payload.branchUrl}/${payload.adjustmentId}`,
          { skipGlobalErrorHandler: true }
        )
        .then(res => res)
        .catch(err => {
          throw err;
        });
    },
    async fetchStockAdjustmentList(
      { commit },
      { clinicUrl, branchUrl, searchKeyword = "" }
    ) {
      try {
        const params = {};
        if (searchKeyword && String(searchKeyword).trim()) {
          params.searchKeyword = String(searchKeyword).trim();
        }

        const res = await axios.get(
          `/api/stockAdjustment/${clinicUrl}/${branchUrl}`,
          { params }
        );
        const items = res.data.data.map(item => ({
          id: item.id,
          clinicId: item.clinicId,
          branchId: item.branchId,
          adjNo: item.adjNo,
          refDocNo: item.refDocNo,
          reason: item.reason,
          status: item.status,
          creationDt: item.creationDt,
          modifyDt: item.modifyDt,
          creationUid: item.creationUid,
          deletedDt: item.deletedDt,
          syncDt: item.syncDt,
          transactionDt: item.transactionDt,
          type: item.type,
          creationUser: item.creationUser,
        }));
        commit("setStockAdjustmentList", items);
        return items;
      } catch (err) {
        console.error("fetchStockAdjustmentList failed", err);
        throw err;
      }
    },
    async fetchStockAdjustmentById(
      { commit },
      { clinicUrl, branchUrl, adjustmentId }
    ) {
      try {
        const res = await axios.get(
          `/api/stockAdjustment/${clinicUrl}/${branchUrl}/${adjustmentId}`
        );

        const data = res?.data?.data || null;
        if (!data) return null;

        return {
          id: data.id,
          adjustmentNo: data.adjustmentNo,
          type: data.type,
          reason: data.reason,
          refDocNo: data.refDocNo || null,
          refDocId: data.refDocId || null,
          status: data.status,
          syncDt: data.syncDt,
          createdDate: data.createdDate,
          transactionDate: data.transactionDt,
          countedDt: data.countedDt,
          createdBy: data.createdBy,
          needHqApproval: Boolean(data.needHqApproval),
          rejectReason: data.rejectReason || null,
          stockLedgers: Array.isArray(data.stockLedgers)
            ? data.stockLedgers.map(item => ({
              id: item.id,
              clinicId: item.clinicId,
              branchId: item.branchId,
              productId: item.productId,
              qtyBaseUom: item.qtyBaseUom,
              qtyUom: item.qtyUom,
              countedQtyBaseUom: item.countedQtyBaseUom ?? null,
              countedQtyUom: item.countedQtyUom ?? null,
              totalQty: item.totalQty,
              uomId: item.uomId,
              status: item.status,
              lotNo: item.lotNo,
              docType: item.docType,
              docId: item.docId,
              docNo: item.docNo,
              totalCost: item.totalCost,
              creationDt: item.creationDt,
              modifyDt: item.modifyDt,
              creationUid: item.creationUid,
              modifyUid: item.modifyUid,
              deletedDt: item.deletedDt,
              productType: item.productType,
              product: item.product || null,
              uom: item.uom || null,
            }))
            : [],
        };
      } catch (err) {
        console.error("fetchStockAdjustmentById failed", err);
        throw err;
      }
    },
    async fetchAllGRWithProductList({ commit }, { clinicUrl, branchUrl }) {
      try {
        const res = await axios.get(
          `/api/goodreceive/${clinicUrl}/${branchUrl}/dropdown`
        );
        const items = res.data.data.map(i => ({
          grNo: i.refDocNo,
          grProduct: i.goodReceiveProducts.map(item => ({
            product: {
              productId: item.product.id,
              productCode: item.product.no,
              productNameMain: item.product.description,
              imageUrl: item.product.file?.thumbnailURL || item.product.file?.mediaURL || null,
            },
            unit: {
              unitId: item.uom.id,
              unitNameTh: item.uom.description,
            },
            stockAvailable: item.qtyAvailable,
            stockOnGR: item.qtyUom,
            remainingReturnableQty: item.remainingReturnableQty,
            productType: item.productType,
          })),
        }));

        commit("setAllGRWithProductList", items);
        return items;
      } catch (err) {
        console.error("fetchAllGRWithProductList failed", err);
        throw err;
      }
    },
    /**
     * @param {Object} payload - { clinicUrl, branchUrl }
     * @returns {Promise<Blob>} - Excel file blob
     */
    exportStockAdjustmentFile(_, { clinicUrl, branchUrl }) {
      return axios
        .get(`/api/stocks/${clinicUrl}/${branchUrl}/export`, {
          responseType: "text",
        })
        .then(async res => {
          const base64 = res.data.trim();
          const dataUrl = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64}`;
          return fetch(dataUrl).then(r => r.blob());
        })
        .catch(err => {
          console.error("Export stock adjustment failed", err);
          throw err;
        });
    },
  },
  getters: {
    getProductList: state => {
      return state.productList;
    },
    getAllGRWithProductList: state => {
      return state.allGRWithProductList;
    },
    getProductListLastUpdated: state => {
      return state.productListLastUpdated;
    },
    getStockAdjustmentList: state => {
      return state.stockAdjustmentList;
    },
    getHQStockAdjustmentList: state => {
      return state.hqStockAdjustmentList;
    },
    isWaitingHqRedDot: state => {
      return state.hqStockAdjustmentList.some(item => item.status === "WAITING_HQ");
    },
    isWaitingBmRedDot: state => {
      return state.stockAdjustmentList.some(item =>
        item.status === "WAITING_BM" || item.status === "PENDING"
      );
    },
  },
};

export default moduleProductInventory;
